/**
 * Hook unificado para persistência de preferências do usuário
 * 
 * Estrutura da chave: `prefs-{userId}-{module}-{sector?}-{key}`
 * 
 * Exemplos:
 * - prefs-abc123-operador-CORTE-viewMode
 * - prefs-abc123-arte-hiddenColumns
 * - prefs-abc123-manager-expandedSections
 * - prefs-abc123-logistics-activeTab
 * - prefs-abc123-global-sound
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PreferenceValue = string | number | boolean | string[] | Record<string, any> | any;

interface UseUserPreferencesOptions {
  module: string;      // 'operador', 'arte', 'comercial', 'logistics', 'manager', 'intelligence', 'global'
  sector?: string;     // 'CORTE', 'IMPRESSAO', etc. (opcional, para operadores)
}

export function useUserPreferences({ module, sector }: UseUserPreferencesOptions) {
  const { user } = useAuth();
  const userId = user?.id || 'anonymous';
  
  // Gerar prefixo da chave
  const keyPrefix = sector 
    ? `prefs-${userId}-${module}-${sector}`
    : `prefs-${userId}-${module}`;
  
  // Cache em memória para evitar leituras repetidas do localStorage
  const cacheRef = useRef<Map<string, PreferenceValue>>(new Map());
  
  // Estado para forçar re-render quando preferências mudam
  const [, setVersion] = useState(0);
  
  // Ler preferência
  const getPreference = useCallback(<T extends PreferenceValue>(
    key: string, 
    defaultValue: T
  ): T => {
    const fullKey = `${keyPrefix}-${key}`;
    
    // Verificar cache
    if (cacheRef.current.has(fullKey)) {
      return cacheRef.current.get(fullKey) as T;
    }
    
    // Ler do localStorage
    try {
      const saved = localStorage.getItem(fullKey);
      if (saved !== null) {
        const parsed = JSON.parse(saved);
        cacheRef.current.set(fullKey, parsed);
        return parsed as T;
      }
    } catch (e) {
      console.warn(`[Preferences] Erro ao ler ${fullKey}:`, e);
    }
    
    // Armazenar default no cache
    cacheRef.current.set(fullKey, defaultValue);
    return defaultValue;
  }, [keyPrefix]);
  
  // Salvar preferência
  const setPreference = useCallback(<T extends PreferenceValue>(
    key: string, 
    value: T
  ) => {
    const fullKey = `${keyPrefix}-${key}`;
    
    try {
      localStorage.setItem(fullKey, JSON.stringify(value));
      cacheRef.current.set(fullKey, value);
      // Forçar re-render
      setVersion(v => v + 1);
    } catch (e) {
      console.warn(`[Preferences] Erro ao salvar ${fullKey}:`, e);
    }
  }, [keyPrefix]);
  
  // Remover preferência
  const removePreference = useCallback((key: string) => {
    const fullKey = `${keyPrefix}-${key}`;
    
    try {
      localStorage.removeItem(fullKey);
      cacheRef.current.delete(fullKey);
      setVersion(v => v + 1);
    } catch (e) {
      console.warn(`[Preferences] Erro ao remover ${fullKey}:`, e);
    }
  }, [keyPrefix]);
  
  // Toggle para arrays (útil para colunas ocultas/colapsadas)
  const toggleInArray = useCallback((key: string, item: string): string[] => {
    const current = getPreference<string[]>(key, []);
    const newValue = current.includes(item)
      ? current.filter(i => i !== item)
      : [...current, item];
    setPreference(key, newValue);
    return newValue;
  }, [getPreference, setPreference]);
  
  // Limpar cache quando userId muda
  useEffect(() => {
    cacheRef.current.clear();
  }, [userId]);
  
  return {
    getPreference,
    setPreference,
    removePreference,
    toggleInArray,
    keyPrefix, // Para debug
  };
}
